import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import CIcon from '@coreui/icons-react'
import {
  CRow,
  CCol,
  CButton,
  CNav,
  CNavItem,
  CNavLink,
  CTabContent,
  CTabPane,
  CBadge,
  CDropdown,
  CDropdownToggle,
  CDropdownMenu,
  CDropdownItem,
} from '@coreui/react'
import {
  cilEnvelopeOpen,
  cilPhone,
  cilLocationPin,
  cilBriefcase,
  cilBuilding,
  cilNotes,
  cilLink,
  cilGlobeAlt,
  cilLockLocked,
  cilStar,
  cilCalendarCheck,
  cilUser,
  cilImage,
  cilWarning,
  cilX,
  cilEducation,
  cilChartLine,
  cilHeart,
  cilSearch,
} from '@coreui/icons'
import { useTranslation } from 'react-i18next'
import { formatDateI18n } from '../utils/i18nFormatters'
import { getMemberPhoneActions } from '../utils/memberPhoneActions'
import PhoneActions from './profile/PhoneActions'
import MemberExclusionModal from './MemberExclusionModal'
import useIsPWA from '../hooks/useIsPWA'
import { useIsModuleEnabled } from '../hooks/useModuleAccess'
import { useEntitlements } from '../contexts/EntitlementsContext'
import NetworkImpactSection from '../views/businessReferrals/NetworkImpactSection'
import MySearches from '../views/jobs/searches/MySearches'
import { canShowSearchesTab } from '../utils/memberSearches'
import Avatar from '../components/shared/Avatar'
import countries from 'world-countries'
import { get, put } from '../utils/apiClient'
import interestsService from '../services/interestsService'
import {
  CAREER_STATUS_BADGE_COLORS,
  getCareerStatusLabel,
} from '../utils/careerStatus'
import { INTERVENTION_ROLE_VALUES, getInterventionRoleLabel } from '../utils/interventionRoles'
import {
  SCHOOL_ROLE_BADGE_COLOR,
  getSchoolRoleLabel,
  sortSchoolRoles,
} from '../utils/schoolRoles'
import { getGenderLabel, formatBirthDate } from '../utils/gender'
import { getSectorLabel } from '../utils/sectors'
import { getMemberTypeLabel } from '../utils/memberType'
import AmbassadorAdminToggle from './admin/AmbassadorAdminToggle'
import SchoolRolesAdminEditor from './admin/SchoolRolesAdminEditor'
import AdminEditProfileButton from './admin/AdminEditProfileButton'
import MemberHistoryButton from './admin/MemberHistoryButton'
import ReferralOpennessCard from './profile/ReferralOpennessCard'
import { isReferralOpen } from '../utils/memberDirectoryFilters'
import './MemberDetailsCard.css'
import TaggedPhotosGallery from './TaggedPhotosGallery'
import MemberExperienceViewer from './MemberExperienceViewer'
import MemberEducationViewer from './MemberEducationViewer'
import MentorTestimonials from '../views/mentoring/MentorTestimonials'

const formatLastLogin = (dateString, t, lang) => {
  if (!dateString) return t('forms:member_card.never_connected')

  const date = new Date(dateString)
  if (isNaN(date.getTime())) return t('forms:member_card.unknown_date')

  // Date + heure localisées (pattern date-fns 'PPp', plus de 'à' FR codé).
  return formatDateI18n(date, 'PPp', lang)
}

const getPrivateValue = (member, field, privateMessage) => {
  if (!member) return null

  if (member.privacy_settings?.[field] === false) {
    return (
      <span className="private-info-badge">
        <CIcon icon={cilLockLocked} size="sm" className="me-1" />
        {privateMessage}
      </span>
    )
  }

  return member[field]
}

const getSafeLinkedInUrl = (url) => {
  if (!url) return null
  try {
    const u = new URL(url.startsWith('http') ? url : `https://${url}`)
    if (!['http:', 'https:'].includes(u.protocol)) return null
    if (!['linkedin.com', 'www.linkedin.com'].includes(u.hostname)) return null
    return u.toString()
  } catch {
    return null
  }
}

// showReferralCard=false : la page Profil rend elle-même la carte « Je peux aider »
// dans sa rangée de cartes ; ailleurs (annuaire) le comportement est inchangé.
const MemberDetailsCard = ({ member: memberProp, isAdmin, onClose, showReferralCard = true }) => {
  const { t, i18n } = useTranslation()
  // Nom de pays localisé (world-countries) : es→spa, en→common (anglais), fr→fra.
  const countryName = (c) =>
    (i18n.language === 'es' ? c?.translations?.spa?.common : null) ||
    (i18n.language === 'en' ? c?.name?.common : null) ||
    c?.translations?.fra?.common ||
    c?.name?.common
  const [showExcludeModal, setShowExcludeModal] = useState(false)
  const [isExcluding, setIsExcluding] = useState(false)
  const [activeTab, setActiveTab] = useState('profile')
  const [fullMember, setFullMember] = useState(null)
  // Rating teaser pour le badge dans le header (chargé seulement si membre est mentor)
  const [mentorRating, setMentorRating] = useState(null)
  // Centres d'intérêt du membre + ceux en commun avec l'utilisateur courant.
  const [interests, setInterests] = useState([])
  const [commonInterests, setCommonInterests] = useState([])
  // Labels des catégories (clé→libellé) + emoji par slug (taxonomie) pour l'onglet.
  const [interestCategories, setInterestCategories] = useState({})
  const [interestEmojis, setInterestEmojis] = useState({})
  const navigate = useNavigate()
  const currentUser = useSelector((state) => state.user?.user)
  const { shouldShowBottomNav } = useIsPWA()
  const { memberTypeConfig, orgType } = useEntitlements()
  // Onglet « Réseau » (Impact réseau) : gaté DEUX sources de vérité — vertical
  // business + module réellement actif. Invisible pour alumni/diaspora.
  const businessReferralsEnabled = useIsModuleEnabled('business_referrals')
  const showNetworkTab = orgType === 'business' && businessReferralsEnabled
  const jobsEnabled = useIsModuleEnabled('jobs')

  // Si le membre passe en prop n'a pas validated (donnees incompletes),
  // charger le profil complet via API
  useEffect(() => {
    if (memberProp?.id && memberProp.validated === undefined) {
      get(`/members/${memberProp.id}`)
        .then(data => setFullMember(data))
        .catch(err => console.error('Erreur chargement profil complet:', err))
    } else {
      setFullMember(null)
    }
  }, [memberProp?.id, memberProp?.validated])

  // Charge le rating teaser dès qu'on a un membre mentor — pour afficher
  // le mini-badge "⭐ X/5 · N avis" dans le header (cliquable → onglet Mentor).
  useEffect(() => {
    const id = memberProp?.id || fullMember?.id
    const isMentor = memberProp?.mentor_available || fullMember?.mentor_available
    if (!id || !isMentor) {
      setMentorRating(null)
      return
    }
    let cancelled = false
    get(`/mentoring/mentors/${id}/testimonials`)
      .then((res) => {
        if (!cancelled) setMentorRating(res?.rating || null)
      })
      .catch(() => {
        if (!cancelled) setMentorRating(null)
      })
    return () => {
      cancelled = true
    }
  }, [memberProp?.id, memberProp?.mentor_available, fullMember?.id, fullMember?.mentor_available])

  const member = fullMember || memberProp

  // Vérifier si c'est notre propre profil
  const isOwnProfile = currentUser && member && Number(currentUser.id) === Number(member.id)

  // Onglet « Mes recherches » (module Emploi) : UNIQUEMENT sur sa propre fiche et module actif. Les recherches
  // des autres membres ne s'affichent jamais ici (elles suivent les règles de visibilité de l'onglet Emploi).
  const showSearchesTab = canShowSearchesTab({ isOwnProfile: Boolean(isOwnProfile), jobsEnabled })

  // Vérifier si le membre peut être contacté (validé ET profil vérifié)
  const canContact = member?.validated && member?.isProfileVerified

  // Actions rapides sur un numéro (WhatsApp / appel / SMS / copie) : même contrôle
  // d'accès que « Envoyer un message », jamais sur son propre profil, et RIEN si
  // le champ est masqué (l'admin reçoit pourtant la valeur, cf. memberPhoneActions).
  const phoneActionsFor = (field) =>
    getMemberPhoneActions({ member, field, canContact: canContact && !isOwnProfile })

  // Centres d'intérêt du membre + intérêts en commun avec l'utilisateur courant
  // (V1 matching). Placé après `member`/`isOwnProfile` (closure au run de l'effet).
  useEffect(() => {
    const id = member?.id
    if (!id) {
      setInterests([])
      setCommonInterests([])
      return undefined
    }
    let cancelled = false
    interestsService
      .getMemberInterests(id)
      .then((list) => {
        if (!cancelled) setInterests(Array.isArray(list) ? list : [])
      })
      .catch(() => {
        if (!cancelled) setInterests([])
      })
    if (!isOwnProfile && currentUser?.id) {
      interestsService
        .getCommonInterests(id)
        .then((list) => {
          if (!cancelled) setCommonInterests(Array.isArray(list) ? list : [])
        })
        .catch(() => {
          if (!cancelled) setCommonInterests([])
        })
    } else {
      setCommonInterests([])
    }
    return () => {
      cancelled = true
    }
  }, [member?.id, isOwnProfile, currentUser?.id])

  // Garde-fou : si l'onglet "interests" est actif mais que le membre n'a plus
  // d'intérêt (changement de fiche, suppression), l'onglet disparaît → on
  // retombe sur Profil pour éviter un pane vide.
  useEffect(() => {
    if (activeTab === 'interests' && interests.length === 0) {
      setActiveTab('profile')
    }
  }, [activeTab, interests.length])

  // Même garde-fou pour l'onglet Réseau (business only) : si le contexte n'est
  // plus business (changement de fiche/vertical), on retombe sur Profil.
  useEffect(() => {
    if (activeTab === 'network' && !showNetworkTab) {
      setActiveTab('profile')
    }
  }, [activeTab, showNetworkTab])

  // Même garde-fou pour « Mes recherches » : fiche d'un autre membre ou module coupé => retour à Profil.
  useEffect(() => {
    if (activeTab === 'searches' && !showSearchesTab) {
      setActiveTab('profile')
    }
  }, [activeTab, showSearchesTab])

  // Labels des catégories d'intérêt (statique) pour grouper l'onglet dédié.
  useEffect(() => {
    let cancelled = false
    interestsService
      .getTaxonomy()
      .then((data) => {
        if (cancelled || !data) return
        if (data.categories) setInterestCategories(data.categories)
        if (Array.isArray(data.interests)) {
          const map = {}
          for (const it of data.interests) if (it.emoji) map[it.slug] = it.emoji
          setInterestEmojis(map)
        }
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  const handleStartConversation = (e) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    console.log('Émission événement createFloatingConversation pour:', member.id)

    // Fermer le modal avant d'ouvrir la conversation
    if (typeof onClose === 'function') {
      onClose()
    }

    // Attendre la fermeture avant d'émettre l'événement
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent('createFloatingConversation', {
          detail: { memberId: Number(member.id) },
        }),
      )
    }, 150)
  }

  useEffect(() => {
    if (member?.id) {
      const today = new Date().toDateString() // "Fri Jun 13 2025"
      const trackingKey = `profile_view_${member.id}_${today}`
      const alreadyTrackedToday = localStorage.getItem(trackingKey)

      if (!alreadyTrackedToday) {
        localStorage.setItem(trackingKey, 'true')

        // Security: userId in localStorage is for UI state only - auth handled via httpOnly cookies
        const userId = localStorage.getItem('userId')
        if (!userId) return

        get(`/members/${member.id}`).catch((error) => {
          console.warn('Erreur tracking:', error)
          localStorage.removeItem(trackingKey)
        })
      }
    }
  }, [member?.id])

  // Nettoyage au montage du composant
  useEffect(() => {
    const cleanOldTrackingKeys = () => {
      const today = new Date().toDateString()
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('profile_view_') && !key.includes(today)) {
          localStorage.removeItem(key)
        }
      })
    }

    cleanOldTrackingKeys()
  }, []) // Une seule fois au montage

  const handleExclude = async () => {
    setIsExcluding(true)
    try {
      await put(`/members/${member.id}/suspend`)
      window.location.reload()
    } catch (error) {
      console.error('Erreur:', error)
    } finally {
      setIsExcluding(false)
      setShowExcludeModal(false)
    }
  }

  return (
    <div className="member-details-container">
      {/* Bouton fermer flottant - visible uniquement en PWA */}
      {shouldShowBottomNav && onClose && (
        <button
          className="pwa-modal-close-btn"
          onClick={onClose}
          aria-label={t('common:close')}
        >
          <CIcon icon={cilX} />
        </button>
      )}

      {/* Header */}
      <div className="profile-header">
        <CRow className="align-items-center">
          <CCol xs={12} md={3} className="text-center text-md-start">
            <div className="profile-avatar-container">
              <Avatar size={150} user={member} className="profile-avatar" />
            </div>
          </CCol>
          <CCol xs={12} md={9} className="text-center text-md-start">
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <div className="profile-name">
                  {member.firstName} {member.lastName}
                </div>
                {/* Badges (rôle, type, statut, rôles d'intervention…) dans un bloc
                    DÉDIÉ sous le nom, avec retour à la ligne (flex-wrap) : évite que
                    de nombreux tags n'élargissent le header et repoussent le bouton
                    « Envoyer un message » hors écran. Centré sur mobile, aligné à
                    gauche dès md — cohérent avec text-center text-md-start du header. */}
                <div className="profile-badges d-flex align-items-center flex-wrap gap-2 mb-2 justify-content-center justify-content-md-start">
                  {member.role === 'admin' && (
                    <span className="admin-badge">{t('forms:profile.tab_admin_badge')}</span>
                  )}
                  {/* Badge Ambassadeur (Sprint Vidal 2026-05-23).
                      Placé juste après Admin = priorité visuelle haute mais
                      sous le rôle technique. Couleur warning (orange) unique
                      pour tous les ambassadeurs — la différenciation passe
                      par le texte `ambassador_title` (libre, contextuel),
                      pas par des couleurs multiples (bruit visuel).
                      Pas de check privacy : un badge ambassadeur est par
                      nature public (sa raison d'être = visibiliser). */}
                  {member.is_ambassador && (() => {
                    const label = member.ambassador_title
                      ? t('forms:member_card.ambassador_titled', { title: member.ambassador_title })
                      : t('forms:member_card.ambassador')
                    return (
                      <CBadge
                        color="warning"
                        className="ms-1"
                        style={{ fontSize: '0.7rem', fontWeight: 500 }}
                        title={label}
                      >
                        {label}
                      </CBadge>
                    )
                  })()}
                  {member.is_terrilink_operator ? (
                    // Compte opérateur Terrilink staff : badge dédié remplace
                    // celui de member_type (qui dirait "Intervenant / Contact
                    // externe" pour member_type='external', faux ici).
                    <CBadge
                      color="dark"
                      className="ms-1"
                      style={{ fontSize: '0.7rem', fontWeight: 500 }}
                      title={t('forms:member_card.staff_terrilink_title')}
                    >
                      {t('forms:member_card.staff_terrilink')}
                    </CBadge>
                  ) : (
                    member.member_type && member.member_type !== 'member' && memberTypeConfig && (
                      <CBadge
                        color={memberTypeConfig.badgeColors?.[member.member_type] || 'light'}
                        className="ms-1"
                        style={{ fontSize: '0.7rem', fontWeight: 500 }}
                      >
                        {getMemberTypeLabel(orgType, member.member_type, t, memberTypeConfig.labels)}
                      </CBadge>
                    )
                  )}
                  {/* Fonction dans l'établissement : 1 badge par rôle.
                      Placé juste après le badge de type, là où apparaissait le
                      badge « Enseignant(e) » issu de member_type='professor' —
                      même position, même couleur `primary`, donc continuité
                      visuelle une fois la bascule jouée.

                      `sortSchoolRoles` et non `.map()` sur le tableau brut :
                      le backfill SQL append en fin de tableau alors que la
                      route admin réordonne, l'ordre en base n'est donc pas
                      fiable. Il filtre aussi les clés hors taxonomie.

                      Pas de gate orgType : la route d'écriture est déjà
                      alumni-only, le tableau est vide ailleurs (même parti
                      pris que les badges intervention_roles). */}
                  {sortSchoolRoles(member.school_roles).map((role) => {
                    const label = getSchoolRoleLabel(role, t)
                    return (
                      <CBadge
                        key={`sr-${role}`}
                        color={SCHOOL_ROLE_BADGE_COLOR}
                        className="ms-1"
                        style={{ fontSize: '0.7rem', fontWeight: 500 }}
                        title={`${t('business:school_roles_meta.badge_title')} — ${label}`}
                      >
                        {label}
                      </CBadge>
                    )
                  })}
                  {/* « Je peux aider » : le serveur ne renvoie le champ que si le profil est découvrable (ou soi-même). */}
                  {isReferralOpen(member) && (
                    <CBadge
                      color="info"
                      className="ms-1"
                      style={{ fontSize: '0.7rem', fontWeight: 500 }}
                      title={t('forms:member_card.referral_open_title')}
                    >
                      {t('forms:member_card.referral_open')}
                    </CBadge>
                  )}
                  {member.mentor_available && (
                    <>
                      <CBadge
                        color="success"
                        className="ms-1"
                        style={{ fontSize: '0.7rem', fontWeight: 500, cursor: 'pointer' }}
                        title={t('forms:member_card.mentor_badge_title')}
                        onClick={() => setActiveTab('mentor')}
                      >
                        {t('forms:member_card.mentor')}
                      </CBadge>
                      {mentorRating?.avg != null && (
                        <CBadge
                          color="warning"
                          className="ms-1"
                          style={{ fontSize: '0.7rem', fontWeight: 500, cursor: 'pointer' }}
                          title={t('forms:member_card.mentor_rating_title')}
                          onClick={() => setActiveTab('mentor')}
                        >
                          {t('forms:member_card.mentor_rating', {
                            avg: mentorRating.avg,
                            count: mentorRating.count,
                          })}
                        </CBadge>
                      )}
                    </>
                  )}
                  {/* career_status badge : silencieux si BADGE_COLORS[value] = null
                      (cas 'not_specified'), respecte privacy_settings.career_status
                      (default true). Labels et couleurs centralisés dans
                      utils/careerStatus. */}
                  {member.privacy_settings?.career_status !== false &&
                    CAREER_STATUS_BADGE_COLORS[member.career_status] && (
                      <CBadge
                        color={CAREER_STATUS_BADGE_COLORS[member.career_status]}
                        className="ms-1"
                        style={{ fontSize: '0.7rem', fontWeight: 500 }}
                        title={getCareerStatusLabel(member.career_status, t)}
                      >
                        {getCareerStatusLabel(member.career_status, t)}
                      </CBadge>
                    )}
                  {/* Rôles d'intervention (alumni) : 1 badge par rôle déclaré.
                      Visible par défaut ; tableau vide sur les non-alumni → rien. */}
                  {Array.isArray(member.intervention_roles) &&
                    member.intervention_roles
                      .filter((role) => INTERVENTION_ROLE_VALUES.includes(role))
                      .map((role) => {
                        const label = getInterventionRoleLabel(role, t)
                        return (
                          <CBadge
                            key={`ir-${role}`}
                            color="info"
                            className="ms-1"
                            style={{ fontSize: '0.7rem', fontWeight: 500 }}
                            title={label}
                          >
                            {label}
                          </CBadge>
                        )
                      })}
                </div>
                <div className="profile-title">
                  {getPrivateValue(member, 'position', t('forms:member_card.position_private'))}
                </div>
                <div className="profile-company">
                  <CIcon icon={cilBuilding} />
                  {getPrivateValue(member, 'company', t('forms:member_card.company_private'))}
                </div>
                {member.primaryEducation?.alumniLabel && (
                  <div className="profile-alumni">
                    <CIcon icon={cilEducation} className="profile-alumni-icon" />
                    {member.primaryEducation.alumniLabel}
                  </div>
                )}
                {member.createdAt && (() => {
                  const d = new Date(member.createdAt)
                  if (isNaN(d.getTime())) return null
                  const formatted = formatDateI18n(d, 'MMMM yyyy', i18n.language)
                  return (
                    <div className="profile-member-since">
                      {t('forms:member_card.member_since', { date: formatted })}
                    </div>
                  )
                })()}
              </div>
              <div className="d-flex flex-column align-items-end gap-2">
                {/* Afficher le bouton Message seulement si ce n'est pas notre propre profil */}
                {!isOwnProfile && (
                  <CButton
                    color="light"
                    className={`message-btn${!canContact ? ' opacity-50' : ''}`}
                    onClick={canContact ? handleStartConversation : undefined}
                    disabled={!canContact}
                    style={{
                      zIndex: 10,
                      position: 'relative',
                      cursor: !canContact ? 'not-allowed' : 'pointer',
                    }}
                    title={
                      !canContact
                        ? t('forms:member_card.cannot_contact_title')
                        : t('forms:member_card.send_message')
                    }
                  >
                    <CIcon icon={cilEnvelopeOpen} className="me-2" />
                    {t('forms:member_card.send_message')}
                  </CButton>
                )}
                {/* Actions admin regroupées dans un menu déroulant — la pile de
                    boutons autonomes (ambassadeur/rôles/édition/historique)
                    devenait trop chargée visuellement à mesure que les lots
                    admin-sur-membre s'accumulaient. "Envoyer un message" reste
                    seul en bouton visible : c'est l'action principale, pas une
                    action d'admin. Chaque item ouvre le même modal qu'avant
                    (renderTrigger transmis au composant, qui garde toute sa
                    logique d'état interne — seul le déclencheur visuel change).
                    Condition d'affichage du menu = union des 4 gardes
                    ci-dessous (au moins un item serait rendu) :
                    orgType==='alumni' seul couvre déjà Ambassadeur+Rôles,
                    !isOwnProfile seul couvre déjà Édition+Historique — pas
                    besoin de répéter chaque combinaison. */}
                {isAdmin && (orgType === 'alumni' || !isOwnProfile) && (
                  // `.profile-header` a `overflow: hidden` (pour clipper le motif
                  // décoratif ::before aux bords de la carte) — un menu déroulant
                  // ouvert vers le bas dépasse la hauteur du header et se faisait
                  // couper net à sa bordure, "disparaissant" derrière la barre
                  // d'onglets qui suit juste en dessous. `portal` fait rendre le
                  // menu dans document.body (React Portal, hors de cet ancêtre à
                  // overflow:hidden) — z-index explicite nécessaire puisqu'il
                  // sort alors du contexte d'empilement de la modale CoreUI
                  // (surchargée à 10500 globalement, cf. reference_zindex_layers) :
                  // sans ça, portalé en root, il retomberait sous cette modale.
                  <CDropdown alignment="end" portal>
                    <CDropdownToggle
                      color="light"
                      size="sm"
                      style={{ zIndex: 10, position: 'relative' }}
                    >
                      {t('forms:member_card.admin_actions_toggle')}
                    </CDropdownToggle>
                    <CDropdownMenu style={{ zIndex: 10510 }}>
                      {/* Ambassadeur — tenants alumni, pas sur son propre profil. */}
                      {orgType === 'alumni' && !isOwnProfile && (
                        <AmbassadorAdminToggle
                          member={member}
                          onUpdate={(ambassadorData) =>
                            setFullMember((prev) => ({
                              ...(prev || memberProp),
                              ...ambassadorData,
                            }))
                          }
                          renderTrigger={({ onClick, label }) => (
                            <CDropdownItem onClick={onClick}>{label}</CDropdownItem>
                          )}
                        />
                      )}
                      {/* Rôles établissement — tenants alumni. PAS de garde
                          !isOwnProfile : une école peut n'avoir qu'un seul
                          admin (le chef d'établissement), et l'en empêcher
                          rendrait le rôle `direction` inatteignable pour lui. */}
                      {orgType === 'alumni' && (
                        <SchoolRolesAdminEditor
                          member={member}
                          onUpdate={(schoolRolesData) =>
                            setFullMember((prev) => ({
                              ...(prev || memberProp),
                              ...schoolRolesData,
                            }))
                          }
                          renderTrigger={({ onClick, label }) => (
                            <CDropdownItem onClick={onClick}>{label}</CDropdownItem>
                          )}
                        />
                      )}
                      {/* Édition de fiche — tous org_type, pas sur son propre
                          profil (self-edit passe par Profil, pas cette route). */}
                      {!isOwnProfile && (
                        <AdminEditProfileButton
                          member={member}
                          onUpdate={(patch) =>
                            setFullMember((prev) => ({ ...(prev || memberProp), ...patch }))
                          }
                          renderTrigger={({ onClick, label }) => (
                            <CDropdownItem onClick={onClick}>{label}</CDropdownItem>
                          )}
                        />
                      )}
                      {/* Historique de parcours — lecture seule, pas sur son
                          propre profil, tous org_type. */}
                      {!isOwnProfile && (
                        <MemberHistoryButton
                          member={member}
                          renderTrigger={({ onClick, label }) => (
                            <CDropdownItem onClick={onClick}>{label}</CDropdownItem>
                          )}
                        />
                      )}
                    </CDropdownMenu>
                  </CDropdown>
                )}
              </div>
            </div>
            {!canContact && (
              <div className="member-not-validated-alert">
                <div className="alert-icon">
                  <CIcon icon={cilWarning} size="sm" />
                </div>
                <div className="alert-content">
                  {!member?.validated ? (
                    <>
                      <div className="alert-title">{t('forms:member_card.pending_title')}</div>
                      <p className="alert-text">
                        {t('forms:member_card.pending_text')}
                      </p>
                    </>
                  ) : !member?.isProfileVerified ? (
                    <>
                      <div className="alert-title">{t('forms:member_card.incomplete_title')}</div>
                      <p className="alert-text">
                        {t('forms:member_card.incomplete_text')}
                      </p>
                    </>
                  ) : null}
                </div>
              </div>
            )}
          </CCol>
        </CRow>
      </div>

      {/* Navigation par onglets */}
      <div className="profile-tabs-container">
        <CNav variant="tabs" className="profile-tabs">
          <CNavItem>
            <CNavLink
              active={activeTab === 'profile'}
              onClick={() => setActiveTab('profile')}
              className="profile-tab"
            >
              <CIcon icon={cilUser} className="tab-icon" />
              <span className="tab-text">{t('forms:member_card.tab_profile')}</span>
            </CNavLink>
          </CNavItem>
          {showNetworkTab && (
            <CNavItem>
              <CNavLink
                active={activeTab === 'network'}
                onClick={() => setActiveTab('network')}
                className="profile-tab"
              >
                <CIcon icon={cilChartLine} className="tab-icon" />
                <span className="tab-text">{t('forms:member_card.tab_network')}</span>
              </CNavLink>
            </CNavItem>
          )}
          <CNavItem>
            <CNavLink
              active={activeTab === 'experiences'}
              onClick={() => setActiveTab('experiences')}
              className="profile-tab"
            >
              <CIcon icon={cilBriefcase} className="tab-icon" />
              <span className="tab-text">{t('forms:member_card.tab_experiences')}</span>
            </CNavLink>
          </CNavItem>
          <CNavItem>
            <CNavLink
              active={activeTab === 'education'}
              onClick={() => setActiveTab('education')}
              className="profile-tab"
            >
              <CIcon icon={cilEducation} className="tab-icon" />
              <span className="tab-text">{t('forms:member_card.tab_education_short')}</span>
            </CNavLink>
          </CNavItem>
          {member.visitedCountries &&
            Array.isArray(member.visitedCountries) &&
            member.visitedCountries.length > 0 && (
              <CNavItem>
                <CNavLink
                  active={activeTab === 'travels'}
                  onClick={() => setActiveTab('travels')}
                  className="profile-tab"
                >
                  <CIcon icon={cilGlobeAlt} className="tab-icon" />
                  <span className="tab-text">{t('forms:member_card.tab_travels')}</span>
                </CNavLink>
              </CNavItem>
            )}
          {interests.length > 0 && (
            <CNavItem>
              <CNavLink
                active={activeTab === 'interests'}
                onClick={() => setActiveTab('interests')}
                className="profile-tab"
              >
                <CIcon icon={cilHeart} className="tab-icon" />
                <span className="tab-text">{t('forms:member_card.tab_interests')}</span>
                {!isOwnProfile && commonInterests.length > 0 && (
                  <CBadge color="success" shape="rounded-pill" className="ms-1">
                    {commonInterests.length}
                  </CBadge>
                )}
              </CNavLink>
            </CNavItem>
          )}
          {member.mentor_available && (
            <CNavItem>
              <CNavLink
                active={activeTab === 'mentor'}
                onClick={() => setActiveTab('mentor')}
                className="profile-tab"
              >
                <CIcon icon={cilStar} className="tab-icon" />
                <span className="tab-text">{t('forms:member_card.tab_mentor')}</span>
              </CNavLink>
            </CNavItem>
          )}
          {showSearchesTab && (
            <CNavItem>
              <CNavLink
                active={activeTab === 'searches'}
                onClick={() => setActiveTab('searches')}
                className="profile-tab"
              >
                <CIcon icon={cilSearch} className="tab-icon" />
                <span className="tab-text">{t('searches:tabs.mine')}</span>
              </CNavLink>
            </CNavItem>
          )}
          <CNavItem>
            <CNavLink
              active={activeTab === 'gallery'}
              onClick={() => setActiveTab('gallery')}
              className="profile-tab"
            >
              <CIcon icon={cilImage} className="tab-icon" />
              <span className="tab-text">{t('forms:member_card.tab_gallery')}</span>
            </CNavLink>
          </CNavItem>
        </CNav>

        <CTabContent className="profile-tab-content">
          {/* Onglet Réseau (business only) — Impact réseau + futur : historique,
              secteurs, opportunités. Le composant s'auto-gate/masque aussi. */}
          {showNetworkTab && (
            <CTabPane visible={activeTab === 'network'}>
              <NetworkImpactSection memberId={member.id} />
            </CTabPane>
          )}
          {/* Onglet Profil */}
          <CTabPane visible={activeTab === 'profile'}>
            <div className="member-info">
              {/* Réglage personnel « Je peux aider » : UNIQUEMENT sur sa propre fiche. */}
              {isOwnProfile && showReferralCard && (
                <ReferralOpennessCard
                  enabled={Boolean(member.referral_openness)}
                  onChange={(value) => setFullMember((prev) => ({ ...(prev || member), referral_openness: value }))}
                />
              )}
              {/* Contact */}
              <div className="member-section">
                <div className="member-section-title">
                  <CIcon icon={cilEnvelopeOpen} />
                  {t('forms:member_card.section_contact')}
                </div>
                {(member.email || member.privacy_settings?.email === false) && (
                  <div className="info-group">
                    <div className="info-content">
                      <div className="info-label">{t('forms:member_card.label_email')}</div>
                      <div className="info-value">
                        {getPrivateValue(member, 'email', t('forms:member_card.email_private'))}
                      </div>
                    </div>
                  </div>
                )}
                {(member.phone || member.privacy_settings?.phone === false) && (
                  <div className="info-group">
                    <div className="info-content">
                      <div className="info-label">{t('forms:member_card.label_phone')}</div>
                      <div className="info-value info-value--actions">
                        <span>
                          {getPrivateValue(member, 'phone', t('forms:member_card.phone_private'))}
                        </span>
                        <PhoneActions actions={phoneActionsFor('phone')} />
                      </div>
                    </div>
                  </div>
                )}
                {(member.mobilePhone || member.privacy_settings?.mobilePhone === false) && (
                  <div className="info-group">
                    <div className="info-content">
                      <div className="info-label">{t('forms:member_card.label_mobile')}</div>
                      <div className="info-value info-value--actions">
                        <span>
                          {getPrivateValue(member, 'mobilePhone', t('forms:member_card.mobile_private'))}
                        </span>
                        <PhoneActions actions={phoneActionsFor('mobilePhone')} />
                      </div>
                    </div>
                  </div>
                )}
                <div className="info-group">
                  <div className="info-content">
                    <div className="info-label">{t('forms:member_card.label_last_login')}</div>
                    <div className="info-value last-login">
                      <CIcon icon={cilCalendarCheck} className="me-2 text-info" />
                      {formatLastLogin(member.lastLogin, t, i18n.language)}
                    </div>
                  </div>
                </div>
                {member.privacy_settings?.linkedin === false ? (
                  <div className="info-group">
                    <div className="info-content">
                      <div className="info-label">{t('forms:member_card.label_linkedin')}</div>
                      <div className="info-value">
                        <span className="private-info-badge">
                          <CIcon icon={cilLockLocked} size="sm" className="me-1" />
                          {t('forms:member_card.linkedin_private')}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : member.linkedin ? (() => {
                  const safeLinkedIn = getSafeLinkedInUrl(member.linkedin)
                  return (
                    <div className="info-group">
                      <div className="info-content">
                        <div className="info-label">{t('forms:member_card.label_linkedin')}</div>
                        <div className="info-value">
                          {safeLinkedIn ? (
                            <a href={safeLinkedIn} target="_blank" rel="noopener noreferrer">
                              <CIcon icon={cilLink} /> {t('forms:member_card.view_profile')}
                            </a>
                          ) : (
                            <span className="text-muted">{member.linkedin}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })() : (
                  // LinkedIn non renseigné et non privé : on n'affiche pas de ligne
                  // « non spécifié » (cohérent avec téléphone/secteur/ville d'origine).
                  null
                )}
              </div>

              {/* Localisation */}
              <div className="member-section">
                <div className="member-section-title">
                  <CIcon icon={cilLocationPin} />
                  {t('forms:member_card.section_location')}
                </div>
                <div className="info-group">
                  <div className="info-content">
                    <div className="info-label">{t('forms:member_card.label_country')}</div>
                    <div className="info-value">
                      <span className="badge-custom">
                        <CIcon icon={cilGlobeAlt} />
                        {member.country || t('forms:member_card.not_specified')}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="info-group">
                  <div className="info-content">
                    <div className="info-label">{t('forms:member_card.label_current_city')}</div>
                    <div className="info-value">{member.city || t('forms:member_card.not_specified')}</div>
                  </div>
                </div>
                {(member.originCity || member.privacy_settings?.originCity === false) && (
                  <div className="info-group">
                    <div className="info-content">
                      <div className="info-label">{t('forms:member_card.label_origin_city')}</div>
                      <div className="info-value">
                        {getPrivateValue(member, 'originCity', t('forms:member_card.origin_city_private'))}
                      </div>
                    </div>
                  </div>
                )}
                {member.department && (
                  <div className="info-group">
                    <div className="info-content">
                      <div className="info-label">{t('forms:member_card.label_department')}</div>
                      <div className="info-value">{member.department}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Colonne 3 : Professionnel + Informations personnelles empilés
                  dans un seul item de grille (.member-col) → pas de cellule vide. */}
              <div className="member-col">
              {/* Professionnel */}
              <div className="member-section">
                <div className="member-section-title">
                  <CIcon icon={cilBriefcase} />
                  {t('forms:member_card.section_professional')}
                </div>
                <div className="info-group">
                  <div className="info-content">
                    <div className="info-label">{t('forms:member_card.label_company')}</div>
                    <div className="info-value">
                      {getPrivateValue(member, 'company', t('forms:member_card.company_private_alt'))}
                    </div>
                  </div>
                </div>
                <div className="info-group">
                  <div className="info-content">
                    <div className="info-label">{t('forms:member_card.label_position')}</div>
                    <div className="info-value">
                      {getPrivateValue(member, 'position', t('forms:member_card.position_private'))}
                    </div>
                  </div>
                </div>
                {/* Secteur : n'affiche la ligne que si renseigné ou explicitement
                    privé (pas de "Secteur non renseigné" qui polluerait la fiche). */}
                {(member.sector || member.privacy_settings?.sector === false) && (
                  <div className="info-group">
                    <div className="info-content">
                      <div className="info-label">{t('forms:member_card.label_sector')}</div>
                      <div className="info-value">
                        {member.privacy_settings?.sector === false
                          ? getPrivateValue(member, 'sector', t('forms:member_card.sector_private'))
                          : getSectorLabel(member.sector, t)}
                      </div>
                    </div>
                  </div>
                )}

                {/* P0f — profil business (« Je recherche » / « Je peux aider sur »)
                    en LECTURE SEULE. Ces propriétés ne sont renvoyées par le backend
                    QUE sur la fiche self d'un tenant business (le composant fait
                    confiance à leur absence ailleurs, sans re-vérifier le self-view).
                    Texte brut échappé par React (jamais dangerouslySetInnerHTML). */}
                {member.business_needs && (
                  <div className="info-group">
                    <div className="info-content">
                      <div className="info-label">{t('business:profile.needs_label')}</div>
                      <div className="info-value" style={{ whiteSpace: 'pre-wrap' }}>
                        {member.business_needs}
                      </div>
                    </div>
                  </div>
                )}
                {member.business_offers && (
                  <div className="info-group">
                    <div className="info-content">
                      <div className="info-label">{t('business:profile.offers_label')}</div>
                      <div className="info-value" style={{ whiteSpace: 'pre-wrap' }}>
                        {member.business_offers}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Informations personnelles : n'affiche la section que si au
                  moins un des deux champs est renseigné ou explicitement privé
                  (sinon on ne pollue pas la fiche des membres sans ces infos). */}
              {((member.gender && member.gender !== 'not_specified') ||
                member.privacy_settings?.gender === false ||
                member.date_of_birth ||
                member.privacy_settings?.date_of_birth === false) && (
                <div className="member-section">
                  <div className="member-section-title">
                    <CIcon icon={cilUser} />
                    {t('forms:member_card.section_personal')}
                  </div>
                  {(member.date_of_birth || member.privacy_settings?.date_of_birth === false) && (
                    <div className="info-group">
                      <div className="info-content">
                        <div className="info-label">{t('forms:member_card.label_birthdate')}</div>
                        <div className="info-value">
                          {member.privacy_settings?.date_of_birth === false ? (
                            <span className="private-info-badge">
                              <CIcon icon={cilLockLocked} size="sm" className="me-1" />
                              {t('forms:member_card.birthdate_private')}
                            </span>
                          ) : (
                            formatBirthDate(member.date_of_birth, i18n.language) || t('forms:member_card.not_specified_f')
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  {((member.gender && member.gender !== 'not_specified') ||
                    member.privacy_settings?.gender === false) && (
                    <div className="info-group">
                      <div className="info-content">
                        <div className="info-label">{t('forms:member_card.label_gender')}</div>
                        <div className="info-value">
                          {member.privacy_settings?.gender === false ? (
                            <span className="private-info-badge">
                              <CIcon icon={cilLockLocked} size="sm" className="me-1" />
                              {t('forms:member_card.gender_private')}
                            </span>
                          ) : (
                            getGenderLabel(member.gender, t) || t('forms:member_card.gender_fallback')
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              </div>
              {/* fin .member-col */}

              {/* Notes */}
              {(member.notes || member.privacy_settings?.notes === false) && (
                <div className="notes-section">
                  <div className="member-section-title">
                    <CIcon icon={cilNotes} />
                    {t('forms:member_card.section_notes')}
                  </div>
                  <div className="info-group">
                    <div className="info-content">
                      <div className="info-value">
                        {getPrivateValue(member, 'notes', t('forms:member_card.notes_private'))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CTabPane>

          {/* Onglet Expériences professionnelles */}
          <CTabPane visible={activeTab === 'experiences'} className="experiences-tab-pane">
            <MemberExperienceViewer memberId={member.id} />
          </CTabPane>

          {/* Onglet Formations */}
          <CTabPane visible={activeTab === 'education'} className="education-tab-pane">
            <MemberEducationViewer memberId={member.id} />
          </CTabPane>

          {/* Onglet Voyages */}
          {member.visitedCountries &&
            Array.isArray(member.visitedCountries) &&
            member.visitedCountries.length > 0 && (
              <CTabPane visible={activeTab === 'travels'} className="travels-tab-pane">
                <div className="travels-container">
                  <h5 className="travels-title">
                    <CIcon icon={cilGlobeAlt} className="me-2" />
                    {t('forms:member_card.travels_title')}
                  </h5>
                  <div className="countries-grid">
                    {member.visitedCountries.map((code) => {
                      const country = countries.find((c) => c.cca2 === code)
                      if (!country) return null

                      return (
                        <div
                          key={code}
                          className="country-badge"
                          title={countryName(country)}
                        >
                          <img
                            src={`https://flagcdn.com/24x18/${code.toLowerCase()}.png`}
                            alt={countryName(country)}
                            className="country-flag"
                          />
                          <span className="country-name">{countryName(country)}</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </CTabPane>
            )}

          {/* Onglet Centres d'intérêt — visible si le membre en a déclaré. */}
          {interests.length > 0 && (
            <CTabPane visible={activeTab === 'interests'} className="interests-tab-pane">
              <div className="interests-container">
                {!isOwnProfile && commonInterests.length > 0 && (
                  <div className="interests-common">
                    <div className="interests-group-title text-success">
                      <CIcon icon={cilHeart} className="me-1" />
                      {t('forms:member_card.common_interests', { count: commonInterests.length })}
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      {commonInterests.map((it) => (
                        <CBadge key={`c-${it.id}`} color="success" className="interest-chip">
                          {interestEmojis[it.slug] ? `${interestEmojis[it.slug]} ` : ''}
                          {it.label}
                        </CBadge>
                      ))}
                    </div>
                  </div>
                )}
                {(() => {
                  const commonSlugs = new Set(commonInterests.map((c) => c.slug))
                  const byCat = {}
                  for (const it of interests) {
                    ;(byCat[it.category] = byCat[it.category] || []).push(it)
                  }
                  // Ordre : catégories de la taxonomie d'abord, puis le reste (custom/'autre').
                  const keys = [...Object.keys(interestCategories), ...Object.keys(byCat)].filter(
                    (k, i, a) => a.indexOf(k) === i && byCat[k]?.length,
                  )
                  return keys.map((cat) => (
                    <div key={cat} className="interests-group">
                      <div className="interests-group-title">
                        {interestCategories[cat] || t('forms:member_card.category_other')}
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        {byCat[cat].map((it) => {
                          const isCommon = commonSlugs.has(it.slug)
                          return (
                            <CBadge
                              key={it.id}
                              color={isCommon ? 'success' : 'light'}
                              textColor={isCommon ? undefined : 'dark'}
                              className="interest-chip"
                            >
                              {interestEmojis[it.slug] ? `${interestEmojis[it.slug]} ` : ''}
                              {it.label}
                            </CBadge>

                          )
                        })}
                      </div>
                    </div>
                  ))
                })()}
              </div>
            </CTabPane>
          )}

          {/* Onglet Mentor — visible si le membre est mentor disponible */}
          {member.mentor_available && (
            <CTabPane visible={activeTab === 'mentor'} className="mentor-tab-pane">
              <div className="member-info">
                <div className="member-section">
                  <div className="member-section-title">
                    <CIcon icon={cilStar} />
                    {t('forms:member_card.section_mentoring')}
                  </div>

                  {/* Bio + topics du mentor (déjà disponibles sur l'objet member) */}
                  {member.mentor_bio && (
                    <div className="info-group">
                      <div className="info-content">
                        <div className="info-label">{t('forms:member_card.label_about')}</div>
                        <div className="info-value" style={{ fontStyle: 'italic' }}>
                          {member.mentor_bio}
                        </div>
                      </div>
                    </div>
                  )}

                  {Array.isArray(member.mentor_topics) && member.mentor_topics.length > 0 && (
                    <div className="info-group">
                      <div className="info-content">
                        <div className="info-label">{t('forms:member_card.label_mentor_topics')}</div>
                        <div className="info-value">
                          {member.mentor_topics.map((topic, i) => (
                            <CBadge
                              key={i}
                              color="light"
                              textColor="dark"
                              className="me-1 mb-1"
                            >
                              {topic}
                            </CBadge>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Témoignages publics + rating moyen */}
                <div className="member-section">
                  <MentorTestimonials
                    mentorId={member.id}
                    onAuthorClick={(author) => {
                      if (!author?.id) return
                      // Pattern existant (cf SocketContext / NotificationHandler) :
                      // route vers /members/:id puis fermer la modal courante.
                      onClose?.()
                      navigate(`/members/${author.id}`)
                    }}
                  />
                </div>
              </div>
            </CTabPane>
          )}

          {/* Onglet Mes recherches : MÊME composant que l'onglet de la page Emploi (aucune logique dupliquée),
              monté seulement quand l'onglet est ouvert (aucun appel API à l'ouverture de la fiche). */}
          {showSearchesTab && (
            <CTabPane visible={activeTab === 'searches'} className="searches-tab-pane">
              {activeTab === 'searches' && <MySearches memberId={member.id} />}
            </CTabPane>
          )}

          {/* Onglet Galerie */}
          <CTabPane visible={activeTab === 'gallery'} className="gallery-tab-pane">
            <TaggedPhotosGallery memberId={member.id} />
          </CTabPane>
        </CTabContent>
      </div>

      <MemberExclusionModal
        visible={showExcludeModal}
        onClose={() => setShowExcludeModal(false)}
        onConfirm={handleExclude}
        memberName={`${member.firstName} ${member.lastName}`}
        isLoading={isExcluding}
      />
    </div>
  )
}

export default MemberDetailsCard
